import * as XLSX from 'xlsx';
import type {
  MaterialsConfig,
  MaterialsEdgeBandingGroup,
  MaterialsEdgeBandingPricing,
  MaterialsEdgeBandingWidth,
} from '../pricing';

export const EDGE_BANDING_PRICING_MATRIX_COLUMNS = [
  { key: 'groupDescription', header: 'Descrição' },
  { key: 'widthMm', header: 'Largura (mm)' },
  { key: 'code', header: 'Código' },
  { key: 'pricePerMeter', header: 'Preço (R$/m)' },
] as const;

export type EdgeBandingPricingMatrixColumnKey = (typeof EDGE_BANDING_PRICING_MATRIX_COLUMNS)[number]['key'];

export const EDGE_BANDING_PRICING_MATRIX_HEADERS = EDGE_BANDING_PRICING_MATRIX_COLUMNS.map((column) => column.header);

export type EdgeBandingPricingMatrixRow = {
  groupId: number | string;
  groupDescription: string;
  widthId: number | string;
  widthMm: number;
  code: string;
  pricePerMeter: number | null;
};

export type EdgeBandingPricingMatrixImportEntry = {
  groupId: number | string;
  widthId: number | string;
  code: string;
  pricePerMeter: number;
};

export type EdgeBandingPricingMatrixParseResult = {
  entries: EdgeBandingPricingMatrixImportEntry[];
  errors: string[];
  skipped: number;
};

const COLUMN_KEY_TO_ROW_FIELD: Record<EdgeBandingPricingMatrixColumnKey, keyof EdgeBandingPricingMatrixRow> = {
  groupDescription: 'groupDescription',
  widthMm: 'widthMm',
  code: 'code',
  pricePerMeter: 'pricePerMeter',
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const normalizeNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const findPricingEntry = (
  pricing: MaterialsEdgeBandingPricing[] | undefined,
  groupId: number | string,
  widthId: number | string,
): MaterialsEdgeBandingPricing | undefined =>
  pricing?.find((entry) => String(entry.group.id) === String(groupId) && String(entry.width.id) === String(widthId));

export const buildEdgeBandingPricingMatrixRows = (
  groups: MaterialsEdgeBandingGroup[] = [],
  widths: MaterialsEdgeBandingWidth[] = [],
  pricing: MaterialsEdgeBandingPricing[] = [],
): EdgeBandingPricingMatrixRow[] => {
  const sortedGroups = [...groups].sort((a, b) =>
    normalizeText(a.description).localeCompare(normalizeText(b.description), 'pt-BR'),
  );
  const sortedWidths = [...widths].sort((a, b) => Number(a.value) - Number(b.value));

  return sortedGroups.flatMap((group) =>
    sortedWidths.map((width) => {
      const existing = findPricingEntry(pricing, group.id, width.id);
      return {
        groupId: group.id,
        groupDescription: normalizeText(group.description),
        widthId: width.id,
        widthMm: Number(width.value),
        code: normalizeText(existing?.code),
        pricePerMeter: existing?.pricePerMeter != null ? Number(existing.pricePerMeter) : null,
      };
    }),
  );
};

export const buildEdgeBandingPricingMatrixRowsFromConfig = (
  config: MaterialsConfig | null | undefined,
): EdgeBandingPricingMatrixRow[] =>
  buildEdgeBandingPricingMatrixRows(config?.edgeBandingGroups, config?.edgeBandingWidths, config?.edgeBandingPricing);

const pricingMatrixRowToCells = (row: EdgeBandingPricingMatrixRow): (string | number)[] =>
  EDGE_BANDING_PRICING_MATRIX_COLUMNS.map((column) => {
    const field = COLUMN_KEY_TO_ROW_FIELD[column.key];
    const value = row[field];

    if (column.key === 'pricePerMeter') {
      return value == null ? '' : Number(value);
    }

    return value as string | number;
  });

export const formatEdgeBandingPricingMatrixExcelBuffer = (
  rows: EdgeBandingPricingMatrixRow[],
  sheetName = 'Matriz fita de borda',
): ArrayBuffer => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    EDGE_BANDING_PRICING_MATRIX_HEADERS,
    ...rows.map(pricingMatrixRowToCells),
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
};

const buildHeaderIndexMap = (headers: string[]): Map<string, number> => {
  const map = new Map<string, number>();

  headers.forEach((header, index) => {
    const normalized = normalizeText(header).toLowerCase();
    map.set(normalized, index);
  });

  return map;
};

const getCellValue = (row: unknown[], index: number | undefined): unknown => {
  if (index == null || index < 0) return undefined;
  return row[index];
};

const findGroup = (groups: MaterialsEdgeBandingGroup[], description: string): MaterialsEdgeBandingGroup | undefined => {
  const normalizedDescription = normalizeText(description).toLowerCase();
  return groups.find((group) => normalizeText(group.description).toLowerCase() === normalizedDescription);
};

const findWidth = (widths: MaterialsEdgeBandingWidth[], widthMm: number): MaterialsEdgeBandingWidth | undefined =>
  widths.find((width) => Number(width.value) === widthMm);

export const parseEdgeBandingPricingMatrixSpreadsheet = (
  buffer: ArrayBuffer,
  config: MaterialsConfig,
): EdgeBandingPricingMatrixParseResult => {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    return { entries: [], errors: ['Planilha vazia.'], skipped: 0 };
  }

  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, defval: '' });

  if (!rows.length) {
    return { entries: [], errors: ['Planilha vazia.'], skipped: 0 };
  }

  const headerRow = rows[0].map((cell) => normalizeText(cell));
  const headerIndex = buildHeaderIndexMap(headerRow);
  const descriptionIndex = headerIndex.get('descrição') ?? headerIndex.get('descricao');
  const widthIndex = headerIndex.get('largura (mm)');
  const codeIndex = headerIndex.get('código') ?? headerIndex.get('codigo');
  const priceIndex =
    headerIndex.get('preço (r$/m)') ?? headerIndex.get('preco (r$/m)') ?? headerIndex.get('preco (r$/m)');

  const missingColumns: string[] = [];
  if (descriptionIndex == null) missingColumns.push('Descrição');
  if (widthIndex == null) missingColumns.push('Largura (mm)');
  if (codeIndex == null) missingColumns.push('Código');
  if (priceIndex == null) missingColumns.push('Preço (R$/m)');

  if (missingColumns.length > 0) {
    return {
      entries: [],
      errors: [`Colunas obrigatórias ausentes: ${missingColumns.join(', ')}.`],
      skipped: 0,
    };
  }

  const groups = config.edgeBandingGroups ?? [];
  const widths = config.edgeBandingWidths ?? [];
  const entries: EdgeBandingPricingMatrixImportEntry[] = [];
  const errors: string[] = [];
  let skipped = 0;

  rows.slice(1).forEach((row, index) => {
    const lineNumber = index + 2;
    const groupDescription = normalizeText(getCellValue(row, descriptionIndex));
    const widthMm = normalizeNumber(getCellValue(row, widthIndex));
    const code = normalizeText(getCellValue(row, codeIndex));
    const pricePerMeter = normalizeNumber(getCellValue(row, priceIndex));

    if (!groupDescription && widthMm == null && !code && pricePerMeter == null) {
      skipped += 1;
      return;
    }

    if (!groupDescription) {
      errors.push(`Linha ${lineNumber}: descrição obrigatória.`);
      return;
    }

    if (widthMm == null) {
      errors.push(`Linha ${lineNumber}: largura inválida.`);
      return;
    }

    const group = findGroup(groups, groupDescription);
    if (!group) {
      errors.push(`Linha ${lineNumber}: grupo "${groupDescription}" não encontrado.`);
      return;
    }

    const width = findWidth(widths, widthMm);
    if (!width) {
      errors.push(`Linha ${lineNumber}: largura ${widthMm} mm não encontrada.`);
      return;
    }

    if (pricePerMeter == null) {
      errors.push(`Linha ${lineNumber}: preço inválido.`);
      return;
    }

    entries.push({
      groupId: group.id,
      widthId: width.id,
      code,
      pricePerMeter,
    });
  });

  return { entries, errors, skipped };
};
